import { FC, useCallback, useEffect } from "react";
import { t } from "i18next";
import { FaTriangleExclamation } from "react-icons/fa6";
import { useSelector } from "react-redux";
import { Alert } from "reactstrap";

import { PAYMENT_OPTIONS } from "@constants/Bookings";
import {
  PaymentMethodSelectorProps,
  PaymentOptionTypes,
} from "@models/services/pricing";
import { RootState } from "@store/index";

import SavedPaymentMethods from "./SavedPaymentMethods/SavedPaymentMethods";

export const PaymentMethodSelector: FC<PaymentMethodSelectorProps> = ({
  disablePaymentOption,
  grandTotal,
  onSelectPaymentMethod,
  paymentOptions,
  selectedPaymentMethod,
}) => {
  const {
    bookingEditData,
    isEditBooking,
    resourcePrices,
    selectedSlot,
    showPaymentCancelWarning,
  } = useSelector((State: RootState) => State.booking);

  const selectedResourceId = selectedSlot?.resourceId;
  const currentResourcePrice = selectedResourceId
    ? resourcePrices?.prices?.[selectedResourceId]
    : undefined;

  const isTokenizationAllowed =
    currentResourcePrice &&
    typeof currentResourcePrice.allowTokenization === "boolean"
      ? currentResourcePrice.allowTokenization
      : true;

  const isTokenizedEditNotAllowed = Boolean(
    isEditBooking &&
      !isTokenizationAllowed &&
      bookingEditData?.tokenizationId !== null &&
      bookingEditData?.tokenizationId !== undefined
  );

  const filteredPaymentOptions =
    isEditBooking &&
    bookingEditData?.payment?.type &&
    bookingEditData?.payment?.type !== PAYMENT_OPTIONS.NONE
      ? paymentOptions.filter(p => p.type === bookingEditData?.payment?.type)
      : paymentOptions;

  const paymentExists = filteredPaymentOptions.some(option => {
    if (option.availableMethods) {
      return option.availableMethods.some(
        m => m.type === bookingEditData?.payment?.type
      );
    }
    return option.type === bookingEditData?.payment?.type;
  });

  const isDisablePaymentOption =
    disablePaymentOption || isTokenizedEditNotAllowed;

  const renderPaymentOption = useCallback(
    (obj: PaymentOptionTypes, paymentType?: string) => {
      const isSelected = selectedPaymentMethod === obj.type;
      const buttonLabel = paymentType ? t(paymentType) : t(obj.type);

      return (
        <button
          className={`btn fg-btn fg-payment-btn ${isDisablePaymentOption ? "disabled" : ""} ${isSelected ? "active" : ""}`}
          key={`${paymentType || ""}-${obj.type}`}
          disabled={isDisablePaymentOption}
          aria-pressed={isSelected}
          aria-label={buttonLabel}
          onClick={() => {
            if (!isDisablePaymentOption) {
              onSelectPaymentMethod(obj.type);
            }
          }}
        >
          <h6 className="mb-0">{buttonLabel}</h6>
          {paymentType && <span>{t(obj?.type)}</span>}
        </button>
      );
    },
    [selectedPaymentMethod, isDisablePaymentOption, onSelectPaymentMethod]
  );

  useEffect(() => {
    if (paymentOptions?.length === 1) {
      onSelectPaymentMethod(paymentOptions[0].type);
    }
  }, [paymentOptions]);

  if (!paymentOptions?.length)
    return (
      <div className="fg-tab-unit-list">
        <div data-testid="paymentMethodNotConfigured">
          <div className="fg-guest-count-form">
            <h5>{t("paymentMethod")}</h5>
            <Alert color="danger" className="small p-2 mb-3">
              {t("paymentMethodNotConfigured")}
            </Alert>
          </div>
        </div>
      </div>
    );

  return (
    <div className="fg-tab-unit-list">
      <div data-testid="pricing-test">
        {selectedPaymentMethod === PAYMENT_OPTIONS.INSTANT_PAYMENT &&
          showPaymentCancelWarning &&
          isTokenizationAllowed && (
            <div
              className="alert alert-warning fg-payment-cancel-warning d-flex align-items-center gap-2 mb-3"
              data-testid="payment-cancel-warning"
            >
              <FaTriangleExclamation className="flex-shrink-0 fg-payment-cancel-warning-icon" />
              <span>
                {t(
                  "instantPaymentCancelWarning",
                  "Instant payment requires saving a payment method for future bookings. To book without saving one, choose Invoice or Cash."
                )}
              </span>
            </div>
          )}
        <div className="fg-guest-count-form">
          <h5>{t("paymentMethod")}</h5>
          {!selectedPaymentMethod && !isDisablePaymentOption && (
            <p className="fg-payment-method-label">
              {t("selectPaymentToProceed")}
            </p>
          )}
          {isTokenizedEditNotAllowed && (
            <Alert color="danger" className="small p-2 mb-3">
              {t(
                "bookingEdit.tokenizedEditNotAllowed",
                "Editing is not allowed for this booking because saved payment methods are not supported for this facility."
              )}
            </Alert>
          )}

          {isEditBooking &&
            bookingEditData?.payment?.type &&
            bookingEditData?.payment?.type !== PAYMENT_OPTIONS.NONE &&
            !paymentExists && (
              <Alert color="danger" className="small p-2 mb-3">
                {t("bookingEdit.paymentSelectorWarning", {
                  paymentmethod: t(bookingEditData?.payment?.type),
                })}
              </Alert>
            )}

          <div className="fg-payment-btn-wrap">
            {filteredPaymentOptions.map(obj => {
              if (!obj?.availableMethods) {
                return renderPaymentOption(obj);
              }

              return obj.availableMethods
                .filter(item => {
                  if (
                    !bookingEditData?.payment?.type ||
                    bookingEditData?.payment?.type === PAYMENT_OPTIONS.NONE
                  )
                    return true;

                  return item.type === bookingEditData.payment.type;
                })
                .map(item => renderPaymentOption(item, obj.type));
            })}
          </div>
          {selectedPaymentMethod === PAYMENT_OPTIONS.INSTANT_PAYMENT &&
            isTokenizationAllowed && (
              <SavedPaymentMethods grandTotal={grandTotal} />
            )}
        </div>
      </div>
    </div>
  );
};

export default PaymentMethodSelector;
